import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { trackEvent } from "@/lib/posthog";
import { fireConfetti, fireConfettiCannons } from "@/lib/confetti";
import {
  Gamepad2, Coins, X, Sparkles, Loader2, Check, RefreshCw,
  Timer as TimerIcon, ArrowUp, ArrowDown,
} from "lucide-react";

/**
 * MiniGameModal — Iter 67 gamification layer.
 *
 * Pops up at the Part 1 boundary of a lesson (≈33% through, well past
 * the 20% trigger spec). Picks a random mechanic so the format never
 * gets stale:
 *   • match  — drag terms onto definitions
 *   • fill   — pick the right word for each blank
 *   • order  — sort items into a sequence
 *   • tf     — 5 true/false statements in 30 seconds
 *
 * Pass threshold = 60% — generous so the dopamine lands fast.
 * Reward = +1 credit, once per (path, step). Counted against the shared
 * WEEKLY_EARNED_CAP via the backend `game:` reason prefix.
 *
 * Replay-for-fun is always available even after the reward is claimed.
 */
export default function MiniGameModal({
  open,
  onOpenChange,
  pathId,
  stepIndex,
  part = 1,
  onRewarded,
}) {
  const [phase, setPhase] = useState("loading"); // loading | playing | result | error
  const [game, setGame] = useState(null);
  const [alreadyRewarded, setAlreadyRewarded] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const submittingRef = useRef(false);

  // Reset & lazy-fetch a game every time the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPhase("loading");
    setGame(null);
    setError(null);
    setResult(null);
    submittingRef.current = false;
    trackEvent("minigame_opened", { path_id: pathId, step_index: stepIndex, part });
    api.post("/games/generate", { path_id: pathId, step_index: stepIndex, part })
      .then(({ data }) => {
        if (cancelled) return;
        setGame(data);
        setAlreadyRewarded(!!data.already_rewarded_for_step);
        setPhase("playing");
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Couldn't build a game — try again.");
        setPhase("error");
      });
    return () => { cancelled = true; };
  }, [open, pathId, stepIndex, part]);

  const submit = async ({ score, total }) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      const { data } = await api.post("/games/complete", {
        game_id: game.game_id,
        path_id: pathId,
        step_index: stepIndex,
        mechanic: game.mechanic,
        score,
        total,
      });
      setResult(data);
      setPhase("result");
      if (data.granted > 0) {
        toast.success(`+${data.granted} credit · nice play!`);
        onRewarded?.(data.credits);
        fireConfettiCannons();
      } else if (data.passed && data.already_rewarded_for_step) {
        toast.message("Nice play — you've already earned the credit for this lesson.");
        fireConfetti({ origin: { y: 0.35 }, particleCount: 50 });
      } else if (!data.passed) {
        toast.message("So close! Replay to try again.");
      }
      trackEvent("minigame_completed", {
        mechanic: game.mechanic, passed: data.passed,
        granted: data.granted, score, total,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't record the game.");
    } finally {
      submittingRef.current = false;
    }
  };

  const replay = () => {
    setPhase("loading");
    setGame(null);
    setError(null);
    setResult(null);
    api.post("/games/generate", { path_id: pathId, step_index: stepIndex, part })
      .then(({ data }) => {
        setGame(data);
        setAlreadyRewarded(!!data.already_rewarded_for_step);
        setPhase("playing");
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || "Couldn't rebuild the game.");
        setPhase("error");
      });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="glass border-white/10 max-w-lg w-[calc(100vw-1.5rem)] p-0 overflow-hidden"
        data-testid="minigame-modal"
      >
        <div className="px-5 pt-5 pb-3 border-b border-white/5 flex items-start gap-3">
          <div className="h-9 w-9 grid place-items-center rounded-xl bg-[#0044FF]/15 border border-[#0044FF]/30 shrink-0">
            <Gamepad2 size={16} className="text-[#9bc7ff]" />
          </div>
          <div className="min-w-0 flex-1">
            <DialogTitle className="font-display text-base flex items-center gap-2">
              Quick break · Mini-game
              {alreadyRewarded ? (
                <span className="text-[10px] font-mono-oak text-white/45 normal-case tracking-normal">
                  Replay for fun
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-mono-oak bg-[#00FF9D]/12 text-[#9bffcc] border border-[#00FF9D]/30 rounded-full px-2 py-0.5"
                  data-testid="minigame-reward-pill"
                >
                  <Coins size={9} /> +1 credit if you pass
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="text-white/55 text-xs mt-0.5">
              {phase === "playing" && game ? game.payload.title :
               phase === "loading" ? "Cooking up a quick game from your lesson…" :
               phase === "error" ? "Couldn't build a game" :
               "How did you do?"}
            </DialogDescription>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-white/45 hover:text-white shrink-0"
            aria-label="Close"
            data-testid="minigame-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 max-h-[68vh] overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
          {phase === "loading" && (
            <div className="py-10 grid place-items-center" data-testid="minigame-loading">
              <Loader2 className="animate-spin text-white/45" size={26} />
              <div className="text-white/50 text-sm mt-3">Generating a fresh game…</div>
            </div>
          )}
          {phase === "error" && (
            <div className="space-y-3" data-testid="minigame-error">
              <div className="text-sm text-rose-200 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
                {error}
              </div>
              <button
                onClick={replay}
                className="w-full py-2.5 rounded-xl btn-primary font-display font-semibold text-sm"
              >
                Try again
              </button>
            </div>
          )}
          {phase === "playing" && game && (
            <GameBody game={game} onSubmit={submit} />
          )}
          {phase === "result" && result && (
            <ResultView result={result} onReplay={replay} onClose={() => onOpenChange(false)} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Game body — switches on mechanic
// =============================================================================

function GameBody({ game, onSubmit }) {
  const p = game.payload;
  if (p.mechanic === "match") return <MatchGame payload={p} onSubmit={onSubmit} />;
  if (p.mechanic === "fill")  return <FillGame payload={p} onSubmit={onSubmit} />;
  if (p.mechanic === "order") return <OrderGame payload={p} onSubmit={onSubmit} />;
  if (p.mechanic === "tf")    return <TFGame payload={p} onSubmit={onSubmit} />;
  return <div className="text-rose-200 text-sm">Unknown game type.</div>;
}

// =============================================================================
// Concept Match — tap a term, then tap a definition
// =============================================================================

function MatchGame({ payload, onSubmit }) {
  // We separate the term & definition columns. User taps a term to
  // activate it, then taps a definition to bind. Wrong matches are
  // possible — score is computed at submit time.
  const [activeTerm, setActiveTerm] = useState(null);
  const [matches, setMatches] = useState({}); // termIdx -> defIdx (shuffled)

  // Shuffle the definitions once so the answer order isn't trivial.
  const defs = useMemo(() => {
    const list = payload.pairs.map((p, i) => ({ text: p.def, originalIdx: i }));
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
  }, [payload]);

  const usedDefs = new Set(Object.values(matches));
  const usedTerms = new Set(Object.keys(matches).map(Number));

  const handleTermClick = (i) => {
    if (usedTerms.has(i)) {
      // unbind
      const next = { ...matches }; delete next[i]; setMatches(next);
      setActiveTerm(null);
      return;
    }
    setActiveTerm(i);
  };
  const handleDefClick = (defShuffledIdx) => {
    if (activeTerm == null) return;
    if (usedDefs.has(defShuffledIdx)) return;
    setMatches({ ...matches, [activeTerm]: defShuffledIdx });
    setActiveTerm(null);
  };

  const allMatched = Object.keys(matches).length === payload.pairs.length;
  const handleSubmit = () => {
    let correct = 0;
    for (const [termIdx, defShuffled] of Object.entries(matches)) {
      // Correct iff the shuffled def's original index equals the term index
      if (defs[defShuffled].originalIdx === Number(termIdx)) correct++;
    }
    onSubmit({ score: correct, total: payload.pairs.length });
  };

  return (
    <div data-testid="game-match">
      <p className="text-xs text-white/50 mb-3">Tap a term, then tap its definition.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-2">
          {payload.pairs.map((p, i) => {
            const isMatched = usedTerms.has(i);
            const isActive = activeTerm === i;
            return (
              <button
                key={i}
                type="button"
                onClick={() => handleTermClick(i)}
                className={`w-full text-left px-3 py-2.5 rounded-xl border text-sm transition ${
                  isActive
                    ? "border-[#00FF9D]/55 bg-[#00FF9D]/12 text-white"
                    : isMatched
                    ? "border-[#0044FF]/40 bg-[#0044FF]/10 text-white"
                    : "border-white/10 bg-white/5 text-white/85 hover:bg-white/10"
                }`}
                data-testid={`game-match-term-${i}`}
              >
                <span className="font-display font-semibold">{p.term}</span>
                {isMatched && (
                  <span className="ml-2 text-[10px] font-mono-oak text-white/55">
                    → {defs[matches[i]].text.slice(0, 22)}…
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="space-y-2">
          {defs.map((d, shuffledIdx) => {
            const isUsed = usedDefs.has(shuffledIdx);
            return (
              <button
                key={shuffledIdx}
                type="button"
                onClick={() => handleDefClick(shuffledIdx)}
                disabled={isUsed || activeTerm == null}
                className={`w-full text-left px-3 py-2.5 rounded-xl border text-xs leading-relaxed transition ${
                  isUsed
                    ? "border-white/5 bg-white/[0.02] text-white/40"
                    : activeTerm != null
                    ? "border-[#00FF9D]/30 bg-white/5 text-white/85 hover:bg-[#00FF9D]/10 hover:border-[#00FF9D]/55 cursor-pointer"
                    : "border-white/8 bg-white/[0.03] text-white/55"
                }`}
                data-testid={`game-match-def-${shuffledIdx}`}
              >
                {d.text}
              </button>
            );
          })}
        </div>
      </div>
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!allMatched}
        className="mt-4 w-full py-2.5 rounded-xl btn-primary font-display font-semibold text-sm disabled:opacity-40"
        data-testid="game-match-submit"
      >
        Submit
      </button>
    </div>
  );
}

// =============================================================================
// Fill-in-the-Blank — radio per item
// =============================================================================

function FillGame({ payload, onSubmit }) {
  const [answers, setAnswers] = useState({}); // i -> option idx

  const allAnswered = Object.keys(answers).length === payload.items.length;
  const handleSubmit = () => {
    let correct = 0;
    payload.items.forEach((it, i) => {
      if (answers[i] === it.answer) correct++;
    });
    onSubmit({ score: correct, total: payload.items.length });
  };

  return (
    <div className="space-y-4" data-testid="game-fill">
      {payload.items.map((it, i) => (
        <div key={i} className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid={`game-fill-q-${i}`}>
          <p className="text-sm text-white/85 leading-relaxed mb-2">
            {it.prompt.split("___").map((part, j, arr) => (
              <span key={j}>
                {part}
                {j < arr.length - 1 && (
                  <span className="inline-block min-w-[60px] px-2 py-0.5 mx-1 rounded bg-[#00FF9D]/12 border border-[#00FF9D]/30 text-[#9bffcc] font-mono-oak text-xs">
                    {answers[i] != null ? it.options[answers[i]] : "____"}
                  </span>
                )}
              </span>
            ))}
          </p>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {it.options.map((opt, j) => (
              <button
                key={j}
                type="button"
                onClick={() => setAnswers({ ...answers, [i]: j })}
                className={`px-3 py-1.5 rounded-lg text-xs border transition ${
                  answers[i] === j
                    ? "border-[#00FF9D]/55 bg-[#00FF9D]/14 text-white"
                    : "border-white/10 bg-white/5 text-white/75 hover:bg-white/10"
                }`}
                data-testid={`game-fill-q-${i}-opt-${j}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!allAnswered}
        className="w-full py-2.5 rounded-xl btn-primary font-display font-semibold text-sm disabled:opacity-40"
        data-testid="game-fill-submit"
      >
        Submit
      </button>
    </div>
  );
}

// =============================================================================
// Order It — up/down buttons to reorder a shuffled list
// =============================================================================

function OrderGame({ payload, onSubmit }) {
  // The payload arrives in CORRECT order; we shuffle for display.
  const [list, setList] = useState(() => {
    const correct = payload.items.map((text, originalIdx) => ({ text, originalIdx }));
    // Shuffle with a guarantee that the result isn't identical to correct
    let shuffled;
    for (let attempts = 0; attempts < 5; attempts++) {
      shuffled = [...correct];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      const same = shuffled.every((it, i) => it.originalIdx === i);
      if (!same) break;
    }
    return shuffled;
  });

  const move = (idx, delta) => {
    const next = [...list];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setList(next);
  };

  const handleSubmit = () => {
    let correct = 0;
    list.forEach((it, i) => { if (it.originalIdx === i) correct++; });
    onSubmit({ score: correct, total: list.length });
  };

  return (
    <div data-testid="game-order">
      <p className="text-xs text-white/50 mb-3">Sort into the correct sequence.</p>
      <ol className="space-y-2">
        {list.map((it, i) => (
          <li
            key={`${it.originalIdx}-${i}`}
            className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2"
            data-testid={`game-order-item-${i}`}
          >
            <span className="text-[10px] font-mono-oak text-white/45 tabular-nums w-5">{i + 1}.</span>
            <span className="flex-1 text-sm text-white/85">{it.text}</span>
            <button
              type="button"
              onClick={() => move(i, -1)}
              disabled={i === 0}
              className="h-7 w-7 grid place-items-center rounded-lg border border-white/10 text-white/55 hover:text-white hover:bg-white/10 disabled:opacity-30"
              aria-label="Move up"
              data-testid={`game-order-up-${i}`}
            >
              <ArrowUp size={12} />
            </button>
            <button
              type="button"
              onClick={() => move(i, 1)}
              disabled={i === list.length - 1}
              className="h-7 w-7 grid place-items-center rounded-lg border border-white/10 text-white/55 hover:text-white hover:bg-white/10 disabled:opacity-30"
              aria-label="Move down"
              data-testid={`game-order-down-${i}`}
            >
              <ArrowDown size={12} />
            </button>
          </li>
        ))}
      </ol>
      <button
        type="button"
        onClick={handleSubmit}
        className="mt-4 w-full py-2.5 rounded-xl btn-primary font-display font-semibold text-sm"
        data-testid="game-order-submit"
      >
        Submit order
      </button>
    </div>
  );
}

// =============================================================================
// Lightning T/F — 30-second countdown
// =============================================================================

function TFGame({ payload, onSubmit }) {
  const TOTAL_SECONDS = 30;
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]); // bool[]
  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
  const submittedRef = useRef(false);

  useEffect(() => {
    if (submittedRef.current) return;
    const t = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(t);
          // Auto-submit when time runs out
          if (!submittedRef.current) {
            submittedRef.current = true;
            finalize(answers);
          }
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finalize = (finalAnswers) => {
    let correct = 0;
    payload.items.forEach((it, i) => {
      if (finalAnswers[i] === it.true) correct++;
    });
    onSubmit({ score: correct, total: payload.items.length });
  };

  const answer = (val) => {
    const next = [...answers]; next[idx] = val; setAnswers(next);
    if (idx + 1 >= payload.items.length) {
      if (!submittedRef.current) {
        submittedRef.current = true;
        finalize(next);
      }
    } else {
      setIdx(idx + 1);
    }
  };

  const current = payload.items[idx];
  return (
    <div data-testid="game-tf">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-mono-oak text-white/55 tabular-nums">
          {idx + 1} / {payload.items.length}
        </div>
        <div
          className={`inline-flex items-center gap-1 text-xs font-mono-oak px-2 py-1 rounded-full border tabular-nums ${
            secondsLeft <= 5
              ? "bg-rose-500/15 border-rose-500/40 text-rose-200"
              : "bg-white/8 border-white/15 text-white/70"
          }`}
          data-testid="game-tf-timer"
        >
          <TimerIcon size={11} /> {secondsLeft}s
        </div>
      </div>
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 mb-3 min-h-[110px]">
        <p className="text-sm text-white/90 leading-relaxed" data-testid="game-tf-prompt">
          {current?.text}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => answer(true)}
          className="py-3 rounded-xl bg-[#00FF9D]/12 border border-[#00FF9D]/40 text-[#9bffcc] font-display font-semibold hover:bg-[#00FF9D]/22 transition"
          data-testid="game-tf-true"
        >
          True
        </button>
        <button
          type="button"
          onClick={() => answer(false)}
          className="py-3 rounded-xl bg-rose-500/12 border border-rose-500/40 text-rose-200 font-display font-semibold hover:bg-rose-500/22 transition"
          data-testid="game-tf-false"
        >
          False
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Result view
// =============================================================================

function ResultView({ result, onReplay, onClose }) {
  const pct = result.total > 0 ? Math.round((result.score / result.total) * 100) : 0;
  const passed = result.passed;
  const granted = result.granted || 0;
  return (
    <div className="text-center" data-testid="minigame-result">
      <div
        className={`mx-auto h-14 w-14 grid place-items-center rounded-full border ${
          passed
            ? "bg-[#00FF9D]/15 border-[#00FF9D]/40 text-[#9bffcc]"
            : "bg-white/5 border-white/15 text-white/55"
        }`}
      >
        {passed ? <Sparkles size={22} /> : <RefreshCw size={20} />}
      </div>
      <h3 className="font-display text-xl mt-3" data-testid="minigame-result-title">
        {passed ? "Nailed it." : "So close!"}
      </h3>
      <p className="text-white/55 text-sm mt-1">
        You scored {result.score} / {result.total} ({pct}%).
      </p>
      {granted > 0 && (
        <div
          className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00FF9D]/15 border border-[#00FF9D]/35 text-[#9bffcc] text-xs font-display font-semibold"
          data-testid="minigame-credit-pill"
        >
          <Coins size={12} /> +{granted} credit earned
        </div>
      )}
      {passed && granted === 0 && (
        <div className="mt-4 text-[11px] text-white/45 max-w-xs mx-auto">
          {result.already_rewarded_for_step
            ? "You've already earned the credit for this lesson — but great recall!"
            : result.weekly_cap_hit
            ? "You've maxed out this week's free credits. Upgrade for unlimited rewards."
            : "Credit not granted — try again next session."}
        </div>
      )}
      <div className="flex gap-2 mt-5">
        <button
          type="button"
          onClick={onReplay}
          className="flex-1 py-2.5 rounded-xl border border-white/15 hover:border-white/30 text-sm font-display"
          data-testid="minigame-replay"
        >
          Replay
        </button>
        <button
          type="button"
          onClick={onClose}
          className="flex-1 py-2.5 rounded-xl btn-primary font-display font-semibold text-sm"
          data-testid="minigame-back-to-lesson"
        >
          Back to lesson
        </button>
      </div>
    </div>
  );
}
